/**
 * @module DemoModeBanner
 * @purpose Full-width, non-dismissible warning bar shown on every screen (including /login)
 *   while the platform is running in Demo Mode.
 * @depends contexts/demo-mode-context (useDemoMode), i18n/hooks/use-formatters
 * @dependedOnBy App.tsx (mounted above AppRoutes, inside BrowserRouter)
 *
 * @rules
 * - Performance: pure display -- status comes from the shared DemoModeContext, no own fetch
 * - State: none
 * - Errors: n/a -- renders null when status is unknown or demo mode is off
 * - Note: no --warning token exists; amber styling uses amber-600 / amber-50 per the
 *   brand-identity semantic-colours table (state = Warning), matching ReasoningStaleBanner
 */

import { useTranslation } from 'react-i18next'
import { useDemoMode } from '../contexts/demo-mode-context'
import { useFormatters } from '../i18n/hooks/use-formatters'

const BANNER_STYLE: React.CSSProperties = {
  background: 'rgba(217, 119, 6, 0.08)',
  borderBottom: '1px solid rgba(217, 119, 6, 0.25)',
  color: 'rgb(146, 64, 14)',
}

const DARK_BANNER_OVERRIDE = 'dark:text-amber-300 dark:border-amber-700/40 dark:bg-amber-900/10'

/**
 * Amber demo-mode warning bar.
 *
 * @behaviour Renders null while the platform status is unknown or demo mode is disabled.
 *            When enabled, shows the standing warning plus an "enabled by / on" line when
 *            the (authenticated) status shape carries `enabledBy` and `enabledAt`.
 * @errors n/a -- purely presentational
 */
export function DemoModeBanner(): React.ReactElement | null {
  const { t } = useTranslation('common')
  const { status } = useDemoMode()
  const { formatDate } = useFormatters()

  if (!status?.demoMode.enabled) {
    return null
  }

  const { enabledBy, enabledAt } = status.demoMode

  return (
    <div
      role="status"
      className={`flex items-center justify-center gap-3 px-3 py-2 text-center text-xs ${DARK_BANNER_OVERRIDE}`}
      style={BANNER_STYLE}
    >
      <span style={{ fontFamily: 'var(--font-body)' }}>{t('demoMode.banner.active')}</span>
      {enabledBy !== undefined && enabledAt !== undefined && (
        <span style={{ fontFamily: 'var(--font-mono)' }}>
          {t('demoMode.banner.enabledBy', { name: enabledBy, date: formatDate(enabledAt) })}
        </span>
      )}
    </div>
  )
}
